function commonPrefixLength(left: string, right: string): number {
  const max = Math.min(left.length, right.length);
  let index = 0;
  while (index < max && left[index] === right[index]) index += 1;
  return index;
}

function suffixPrefixOverlap(base: string, incoming: string): number {
  const max = Math.min(base.length, incoming.length);
  for (let size = max; size > 0; size -= 1) {
    if (base.slice(-size) === incoming.slice(0, size)) return size;
  }
  return 0;
}

/**
 * Merges mixed streaming chunk styles safely.
 *
 * Backends can alternate between:
 * - cumulative chunks ("full text so far")
 * - delta chunks ("new token/text only")
 */
export function mergeStreamingContent(current: string, incoming: string): string {
  if (!incoming) return current;
  if (!current) return incoming;

  if (incoming === current) return current;

  // Cumulative payload (authoritative full text).
  if (incoming.startsWith(current)) return incoming;

  // Repeated delta that is already present at the end.
  if (current.endsWith(incoming)) return current;

  // Incoming text is fully contained within the current text (duplicate
  // from RunContent + TeamRunContent emitting the same content).
  if (current.includes(incoming)) return current;

  // If it's mostly the same prefix and similar size, prefer the incoming text
  // to avoid duplicated words when providers re-emit stitched content.
  const prefixLen = commonPrefixLength(current, incoming);
  if (prefixLen >= 24 && incoming.length >= Math.floor(current.length * 0.8)) {
    return incoming;
  }

  // Incoming is a longer string that fully contains the current text
  // (cumulative payload that doesn't share a prefix but contains all current content).
  if (incoming.includes(current) && incoming.length > current.length) {
    return incoming;
  }

  const overlap = suffixPrefixOverlap(current, incoming);
  if (overlap > 0) return current + incoming.slice(overlap);

  // Delta payload without overlap.
  return current + incoming;
}

export default mergeStreamingContent;
